'use client'

import { Button } from '@/components/ui/button'
import Image from 'next/image'
import { LANDING_PAGE_WIDTH_CONSTRAINTS } from '@/features/landing-page/helpers/landing-page-constants'
import { useSignOut } from '@/hooks/use-sign-out'
import { PUBLIC_LANDING_ROUTES, UrlConfig } from '@/setup/routing/routing'
import { AUTH_STEPS } from '@/features/auth/helpers/auth-constants'
import { cn } from '@/utils/styles/cn'
import { useAuth } from '@clerk/nextjs'
import { AnimatePresence, motion } from 'framer-motion'
import { Menu, X, Smartphone, Globe, ShoppingCart, Video, Image as ImageIcon, ShoppingBag } from 'lucide-react'
import {
    SOLUTION_ITEMS_LEFT,
    SOLUTION_ITEMS_RIGHT,
    SOLUTION_ITEMS_CONTENT,
    SOLUTION_ITEMS_INTEGRATION,
} from '@/features/landing-page/helpers/landing-solutions'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DimensionsFadeContainer } from '@/features/animations/dimensions-fade-container'
import { useLandingAuthContext } from '@/features/landing-page/landing-auth-context'

export const NAV_BAR_HEIGHT = 55

export const LandingPageNavBar = () => {
    const { isSignedIn } = useAuth()
    const { signOut } = useSignOut({
        redirectUrl: UrlConfig['/'].getUrl(),
    })
    const router = useRouter()
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const { handleCtaClick, openAuthModal } = useLandingAuthContext()
    const showSolutions = true

    const handleSecondaryCtaClick = useCallback(() => {
        if (isSignedIn) {
            signOut()
            return
        }

        openAuthModal(AUTH_STEPS.SignIn)
    }, [isSignedIn, signOut, openAuthModal])

    const [isSolutionsOpen, setIsSolutionsOpen] = useState(false)
    const solutionsCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const openSolutions = useCallback(() => {
        if (solutionsCloseTimeoutRef.current) clearTimeout(solutionsCloseTimeoutRef.current)
        setIsSolutionsOpen(true)
    }, [])

    const scheduleCloseSolutions = useCallback(() => {
        if (solutionsCloseTimeoutRef.current) clearTimeout(solutionsCloseTimeoutRef.current)
        // Small delay prevents "gap" flicker between trigger and popover.
        solutionsCloseTimeoutRef.current = setTimeout(() => setIsSolutionsOpen(false), 150)
    }, [])

    const LEFT_ICONS: Record<string, React.ElementType> = {
        'Mobile App Ads': Smartphone,
        'E-Commerce Ads': ShoppingCart,
        'Website Ads': Globe,
    }

    const CONTENT_ICONS: Record<string, React.ElementType> = {
        'AI UGC Ads': Video,
        'Static Ads': ImageIcon,
        'E-Commerce Assets': ShoppingBag,
    }

    const solutionPopover = useMemo(() => {
        return {
            left: SOLUTION_ITEMS_LEFT.map((item) => ({
                ...item,
                Icon: LEFT_ICONS[item.title] ?? Globe,
            })),
            right: SOLUTION_ITEMS_RIGHT.map((item) => ({
                ...item,
                ...(item.iconSrc ? { iconSrc: item.iconSrc } : {}),
            })) as (
                | { title: string; subtitle: string; href: string; Icon: React.ElementType }
                | { title: string; subtitle: string; href: string; iconSrc: string }
            )[],
            content: SOLUTION_ITEMS_CONTENT.map((item) => ({
                ...item,
                Icon: CONTENT_ICONS[item.title] ?? Video,
            })),
            integration: SOLUTION_ITEMS_INTEGRATION.map((item) => ({
                ...item,
                ...(item.iconSrc ? { iconSrc: item.iconSrc } : {}),
            })) as { title: string; subtitle: string; href: string; iconSrc: string }[],
        }
    }, [])

    const popoverRef = useRef<HTMLDivElement>(null)
    const [isPopoverStacked, setIsPopoverStacked] = useState(false)
    const [popoverShift, setPopoverShift] = useState(0)

    useEffect(() => {
        if (!isSolutionsOpen) {
            setPopoverShift(0)
            return
        }

        const HORIZONTAL_MIN_WIDTH = 950
        const MARGIN = 16

        const adjust = () => {
            setIsPopoverStacked(window.innerWidth < HORIZONTAL_MIN_WIDTH)

            requestAnimationFrame(() => {
                const el = popoverRef.current
                if (!el) return
                const rect = el.getBoundingClientRect()
                const vw = window.innerWidth
                let shift = 0
                if (rect.left < MARGIN) shift = MARGIN - rect.left
                else if (rect.right > vw - MARGIN) shift = vw - MARGIN - rect.right
                setPopoverShift(shift)
            })
        }

        adjust()
        window.addEventListener('resize', adjust)
        return () => window.removeEventListener('resize', adjust)
    }, [isSolutionsOpen])

    const navLinks = [
        { label: 'Case Studies', href: '/#case-studies', scrollTo: 'case-studies' },
        { label: 'Pricing', href: PUBLIC_LANDING_ROUTES.PRICING },
    ]

    const onboardingStatusButtonCopy = useMemo(() => {
        if (!isSignedIn) {
            return { primary: 'Get started free', secondary: 'Log in' }
        }

        return { primary: 'Go to Dashboard', secondary: 'Sign Out' }
    }, [isSignedIn])

    const handleCtaSubmit = useCallback(() => {
        handleCtaClick()
    }, [handleCtaClick])

    const handleLogoClick = useCallback(
        (e: React.MouseEvent) => {
            if (isSignedIn) {
                e.preventDefault()
                router.push(UrlConfig['/routing'].getUrl())
            }
        },
        [isSignedIn, router]
    )

    return (
        <>
            <div
                className="fixed top-0 left-0 z-40 w-full bg-white/70 backdrop-blur-xl"
                style={{
                    height: NAV_BAR_HEIGHT,
                }}
            >
                <div className={cn(LANDING_PAGE_WIDTH_CONSTRAINTS, 'h-full')}>
                    {/* Mobile header */}
                    <div className="flex items-center h-full md:hidden">
                        <Link href={UrlConfig['/'].getUrl()} onClick={handleLogoClick} className="shrink-0">
                            <Image src="/Superscale-Logo-black.svg" alt="Superscale" width={120} height={22} />
                        </Link>

                        <div className="ml-auto flex items-center gap-2">
                            <Button
                                variant="ds2-primary"
                                size="ds2"
                                onClick={handleCtaSubmit}
                                data-cta="get_started"
                                data-section="nav"
                            >
                                <DimensionsFadeContainer>{onboardingStatusButtonCopy.primary}</DimensionsFadeContainer>
                            </Button>

                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="p-2 text-gray-700 hover:text-gray-900 transition-colors"
                                aria-label="Toggle menu"
                            >
                                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                            </button>
                        </div>
                    </div>

                    {/* Desktop header */}
                    <div className="hidden md:grid grid-cols-3 items-center h-full">
                        {/* Left: logo */}
                        <div className="flex items-center gap-2">
                            <Link href={UrlConfig['/'].getUrl()} onClick={handleLogoClick}>
                                <Image src="/Superscale-Logo-black.svg" alt="Superscale" width={120} height={22} />
                            </Link>
                        </div>

                        {/* Center: nav */}
                        <div className="flex items-center justify-center">
                            <nav className="flex items-center gap-3 whitespace-nowrap">
                                {showSolutions && (
                                    <div
                                        className="relative"
                                        onMouseEnter={openSolutions}
                                        onMouseLeave={scheduleCloseSolutions}
                                    >
                                        <button
                                            className={cn(
                                                'flex items-center gap-1 text-sm font-medium text-black/65 hover:text-black transition-colors',
                                                'px-3 py-1.5 rounded-[10px]',
                                                'hover:bg-black/4'
                                            )}
                                            style={
                                                isSolutionsOpen ? { backgroundColor: 'rgba(0,0,0,0.04)' } : undefined
                                            }
                                        >
                                            Solutions
                                        </button>
                                        {/* Hover bridge so moving through the gap doesn't close the popover */}
                                        {isSolutionsOpen && (
                                            <div className="absolute top-full left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)] max-w-[900px] h-4" />
                                        )}
                                        <AnimatePresence>
                                            {isSolutionsOpen && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 8 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: 8 }}
                                                    transition={{ duration: 0.15 }}
                                                    onMouseEnter={openSolutions}
                                                    onMouseLeave={scheduleCloseSolutions}
                                                    ref={popoverRef}
                                                    style={{ marginLeft: popoverShift }}
                                                    className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-auto rounded-2xl bg-white shadow-[0_12px_40px_rgba(0,0,0,0.18)] ring-1 ring-black/10 p-4 z-50"
                                                >
                                                    <div
                                                        className={cn(
                                                            'flex gap-4',
                                                            isPopoverStacked ? 'flex-col' : 'flex-row gap-6'
                                                        )}
                                                    >
                                                        <div
                                                            className={cn(
                                                                'min-w-0',
                                                                !isPopoverStacked && 'min-w-[460px] flex-1'
                                                            )}
                                                        >
                                                            <div className="px-2 pb-2">
                                                                <span className="ds-nav-popover-heading">
                                                                    By use case
                                                                </span>
                                                            </div>

                                                            <div className="grid grid-cols-[auto_auto] gap-y-1 gap-x-1">
                                                                <div className="flex flex-col gap-0.5">
                                                                    {solutionPopover.left.map(
                                                                        ({ title, subtitle, href, Icon }) => (
                                                                            <Link
                                                                                key={title}
                                                                                href={href}
                                                                                prefetch={false}
                                                                                onClick={() =>
                                                                                    setIsSolutionsOpen(false)
                                                                                }
                                                                                className={cn(
                                                                                    'flex w-full min-h-[56px] items-center gap-3',
                                                                                    'rounded-xl px-3 py-2 hover:bg-black/3 transition-colors',
                                                                                    'self-stretch'
                                                                                )}
                                                                            >
                                                                                <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-black/4 text-black/70">
                                                                                    <Icon className="size-4" />
                                                                                </div>
                                                                                <div className="min-w-0">
                                                                                    <div className="ds-nav-popover-title truncate">
                                                                                        {title}
                                                                                    </div>
                                                                                    <div className="ds-nav-popover-subtitle truncate">
                                                                                        {subtitle}
                                                                                    </div>
                                                                                </div>
                                                                            </Link>
                                                                        )
                                                                    )}
                                                                </div>

                                                                <div className="flex flex-col gap-0.5">
                                                                    {solutionPopover.right.map((item) => (
                                                                        <Link
                                                                            key={item.title}
                                                                            href={item.href}
                                                                            prefetch={false}
                                                                            onClick={() => setIsSolutionsOpen(false)}
                                                                            className={cn(
                                                                                'flex w-full min-h-[56px] items-center gap-3',
                                                                                'rounded-xl px-3 py-2 hover:bg-black/3 transition-colors',
                                                                                'self-stretch'
                                                                            )}
                                                                        >
                                                                            <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-black/4 text-black/70">
                                                                                {'Icon' in item ? (
                                                                                    <item.Icon className="size-4" />
                                                                                ) : (
                                                                                    <img
                                                                                        src={item.iconSrc}
                                                                                        alt=""
                                                                                        width={16}
                                                                                        height={16}
                                                                                        className="size-4"
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                            <div className="min-w-0">
                                                                                <div className="ds-nav-popover-title truncate">
                                                                                    {item.title}
                                                                                </div>
                                                                                <div className="ds-nav-popover-subtitle truncate">
                                                                                    {item.subtitle}
                                                                                </div>
                                                                            </div>
                                                                        </Link>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {isPopoverStacked ? (
                                                            <div className="h-px bg-black/8 w-full" />
                                                        ) : (
                                                            <div className="w-px bg-black/8 self-stretch" />
                                                        )}

                                                        <div className="flex gap-6">
                                                            <div className="min-w-[180px]">
                                                                <div className="px-2 pb-2">
                                                                    <span className="ds-nav-popover-heading">
                                                                        By content
                                                                    </span>
                                                                </div>

                                                                <div className="flex flex-col gap-0.5">
                                                                    {solutionPopover.content.map(
                                                                        ({ title, subtitle, href, Icon }) => (
                                                                            <Link
                                                                                key={title}
                                                                                href={href}
                                                                                prefetch={false}
                                                                                onClick={() =>
                                                                                    setIsSolutionsOpen(false)
                                                                                }
                                                                                className={cn(
                                                                                    'flex w-full min-h-[56px] items-center gap-3',
                                                                                    'rounded-xl px-3 py-2 hover:bg-black/3 transition-colors',
                                                                                    'self-stretch'
                                                                                )}
                                                                            >
                                                                                <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-black/4 text-black/70">
                                                                                    <Icon className="size-4" />
                                                                                </div>
                                                                                <div className="min-w-0">
                                                                                    <div className="ds-nav-popover-title truncate">
                                                                                        {title}
                                                                                    </div>
                                                                                    <div className="ds-nav-popover-subtitle truncate">
                                                                                        {subtitle}
                                                                                    </div>
                                                                                </div>
                                                                            </Link>
                                                                        )
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="w-px bg-black/8 self-stretch" />

                                                            <div className="min-w-[180px]">
                                                                <div className="px-2 pb-2">
                                                                    <span className="ds-nav-popover-heading">
                                                                        By integration
                                                                    </span>
                                                                </div>

                                                                <div className="flex flex-col gap-0.5">
                                                                    {solutionPopover.integration.map(
                                                                        ({ title, subtitle, href, iconSrc }) => (
                                                                            <Link
                                                                                key={title}
                                                                                href={href}
                                                                                prefetch={false}
                                                                                onClick={() =>
                                                                                    setIsSolutionsOpen(false)
                                                                                }
                                                                                className={cn(
                                                                                    'flex w-full min-h-[56px] items-center gap-3',
                                                                                    'rounded-xl px-3 py-2 hover:bg-black/3 transition-colors',
                                                                                    'self-stretch'
                                                                                )}
                                                                            >
                                                                                <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-black/4 text-black/70">
                                                                                    <img
                                                                                        src={iconSrc}
                                                                                        alt=""
                                                                                        width={16}
                                                                                        height={16}
                                                                                        className="size-4"
                                                                                    />
                                                                                </div>
                                                                                <div className="min-w-0">
                                                                                    <div className="ds-nav-popover-title truncate">
                                                                                        {title}
                                                                                    </div>
                                                                                    <div className="ds-nav-popover-subtitle truncate">
                                                                                        {subtitle}
                                                                                    </div>
                                                                                </div>
                                                                            </Link>
                                                                        )
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}

                                {navLinks.map((link) => (
                                    <Link
                                        key={link.label}
                                        href={link.href}
                                        prefetch={false}
                                        onClick={
                                            'scrollTo' in link && link.scrollTo
                                                ? (e) => {
                                                      const el = document.getElementById(link.scrollTo as string)
                                                      if (el) {
                                                          e.preventDefault()
                                                          el.scrollIntoView({ behavior: 'smooth' })
                                                      }
                                                  }
                                                : undefined
                                        }
                                        className={cn(
                                            'text-sm font-medium text-black/65 hover:text-black transition-colors',
                                            'px-3 py-1.5 rounded-[10px]',
                                            'hover:bg-black/4'
                                        )}
                                    >
                                        {link.label}
                                    </Link>
                                ))}
                            </nav>
                        </div>

                        {/* Right: actions */}
                        <div className="flex items-center justify-end gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleSecondaryCtaClick}
                                aria-label={onboardingStatusButtonCopy.secondary}
                                className="rounded-[10px] border-black/10 bg-white/60 transition-colors duration-200 ease-out hover:bg-black/4"
                            >
                                <DimensionsFadeContainer>
                                    {onboardingStatusButtonCopy.secondary}
                                </DimensionsFadeContainer>
                            </Button>

                            <Button
                                variant="ds2-primary"
                                size="ds2"
                                onClick={handleCtaSubmit}
                                data-cta="get_started"
                                data-section="nav"
                                aria-label={onboardingStatusButtonCopy.primary}
                                className="rounded-[10px]"
                            >
                                <DimensionsFadeContainer>{onboardingStatusButtonCopy.primary}</DimensionsFadeContainer>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Dropdown */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <>
                        {/* Backdrop overlay to close menu on click outside */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 z-30 md:hidden"
                            style={{ top: NAV_BAR_HEIGHT }}
                            onClick={() => setIsMobileMenuOpen(false)}
                        />
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="fixed left-0 right-0 z-40 md:hidden bg-white/95 backdrop-blur-md border-b border-black/10 shadow-lg"
                            style={{ top: NAV_BAR_HEIGHT }}
                        >
                            <div className={cn(LANDING_PAGE_WIDTH_CONSTRAINTS, 'py-4')}>
                                <nav className="flex flex-col gap-1">
                                    {/* Solutions Section */}
                                    {showSolutions && (
                                        <>
                                            <div className="px-3 py-2">
                                                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                                    Solutions
                                                </span>
                                            </div>
                                            {[
                                                ...solutionPopover.left,
                                                ...solutionPopover.right,
                                                ...solutionPopover.integration,
                                            ].map((link) => (
                                                <Link
                                                    key={link.title}
                                                    href={link.href}
                                                    prefetch={false}
                                                    aria-label={link.title}
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                    className="text-left text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors px-3 py-2.5 rounded-lg pl-6"
                                                >
                                                    {link.title}
                                                </Link>
                                            ))}

                                            <div className="my-2 border-t border-gray-100" />
                                        </>
                                    )}

                                    {navLinks.map((link) => (
                                        <Link
                                            key={link.label}
                                            href={link.href}
                                            prefetch={false}
                                            aria-label={link.label}
                                            onClick={(e) => {
                                                if ('scrollTo' in link && link.scrollTo) {
                                                    const el = document.getElementById(link.scrollTo as string)
                                                    if (el) {
                                                        e.preventDefault()
                                                        el.scrollIntoView({ behavior: 'smooth' })
                                                    }
                                                }
                                                setIsMobileMenuOpen(false)
                                            }}
                                            className="text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors px-3 py-2.5 rounded-lg"
                                        >
                                            {link.label}
                                        </Link>
                                    ))}
                                </nav>

                                <div className="mt-4 pt-4 border-t border-gray-100 px-3">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            handleSecondaryCtaClick()
                                            setIsMobileMenuOpen(false)
                                        }}
                                        className="w-full rounded-[10px] border-black/10 bg-white/60 transition-colors duration-200 ease-out hover:bg-black/4"
                                        aria-label={onboardingStatusButtonCopy.secondary}
                                    >
                                        <DimensionsFadeContainer>
                                            {onboardingStatusButtonCopy.secondary}
                                        </DimensionsFadeContainer>
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    )
}
